/**
 * Pricing → Dashboard (`/pricing`).
 *
 * Resumen visual del servicio de precios: KPIs del histórico, evolución del
 * precio promedio por día, comparación por producto y participación por
 * categoría. Usa el mismo filtro de zona (ubigeo) y periodo (año/mes) que
 * Estadísticas, así que las cifras y las gráficas hablan siempre de la zona
 * elegida.
 *
 * Las gráficas son SVG/CSS propios (el panel no trae librería de gráficos); los
 * datos salen de `GET /api/pricing/estadisticas` y el estado y los ficheros de
 * `GET /api/pricing/dashboard`.
 */

import {
  ArrowLeft,
  ArrowUpRight,
  BarChart3,
  Building2,
  CircleDollarSign,
  ClipboardList,
  Clock,
  Database,
  FileText,
  Gauge,
  LineChart,
  MapPin,
  PieChart,
  ServerCog,
  Settings,
  Tags,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import { FiltrosUbicacionCard } from '../components/filtros-ubicacion';
import { GraficoBarras } from '../components/grafico-barras';
import { GraficoDona } from '../components/grafico-dona';
import { GraficoEvolucion } from '../components/grafico-evolucion';
import { useFiltrosUbicacion } from '../hooks/use-filtros-ubicacion';
import { fechaCorta, tamanoLegible } from '../lib/periodos';
import { usePricingDashboard, usePricingEstadisticas } from '../pricing.api';

/** Pantallas de la categoría, como los accesos del panel anterior. */
const ACCESOS = [
  {
    to: '/pricing/precios',
    titulo: 'Precios',
    detalle: 'Competencia, detector de cambios y precios por ubicación',
    icono: Tags,
  },
  {
    to: '/pricing/estadisticas',
    titulo: 'Estadísticas',
    detalle: 'Promedios y distribución por producto y categoría',
    icono: BarChart3,
  },
  {
    to: '/pricing/scraping',
    titulo: 'Consultas',
    detalle: 'Consultas en curso, progreso y ficheros generados',
    icono: ClipboardList,
  },
  {
    to: '/pricing/configuracion',
    titulo: 'Configuración',
    detalle: 'Consulta programada por ubicación y hora',
    icono: Settings,
  },
] as const;

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

const TONOS = {
  brand: 'bg-brand-50 text-brand-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  sky: 'bg-sky-50 text-sky-600',
} as const;

/** Indicador de la fila superior. */
function Indicador({
  titulo,
  valor,
  detalle,
  icono: Icono,
  tono,
}: {
  titulo: string;
  valor: string;
  detalle: string;
  icono: LucideIcon;
  tono: keyof typeof TONOS;
}) {
  return (
    <div className="rounded-card border border-ink-100 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            {titulo}
          </span>
          <span className="mt-1 block truncate text-xl font-bold tabular-nums text-ink-900">
            {valor}
          </span>
          <span className="mt-0.5 block text-[11px] text-ink-400">{detalle}</span>
        </div>
        <span
          className={cn('flex size-9 shrink-0 items-center justify-center rounded-full', TONOS[tono])}
          aria-hidden="true"
        >
          <Icono className="size-4" />
        </span>
      </div>
    </div>
  );
}

export function PricingDashboardPage() {
  useDocumentTitle('Pricing · Dashboard');

  const filtro = useFiltrosUbicacion();
  const panel = usePricingDashboard();
  const consulta = usePricingEstadisticas(filtro.filtros);

  const estado = panel.data?.estado;
  const archivos = panel.data?.archivos;
  const datos = consulta.data?.estadisticas;

  const categorias = Object.entries(datos?.categorias ?? {});
  const serie = datos?.serie ?? [];
  const sinDatos = !consulta.isLoading && (datos?.totalCargas ?? 0) === 0;
  // Se espera también al estado del servicio para no pintar «0 ficheros».
  const cargando = (consulta.isLoading && !datos) || (panel.isLoading && !panel.data);

  // Ficheros de la zona y el periodo elegidos (el listado llega completo).
  const tokens = [filtro.filtros.departamento, filtro.filtros.provincia, filtro.filtros.distrito]
    .filter(Boolean)
    .map((valor) => String(valor).toUpperCase());

  const archivosFiltrados = (archivos?.files ?? []).filter((fichero) => {
    const ubicacion = String(fichero.ubicacion || '').toUpperCase();

    if (!tokens.every((token) => ubicacion.includes(token))) return false;

    const fecha = String(fichero.filename || '').slice(0, 10);

    if (filtro.anio && !fecha.startsWith(filtro.anio)) return false;
    if (filtro.mes && fecha.slice(5, 7) !== filtro.mes) return false;

    return true;
  });

  const tamanoFiltrado = archivosFiltrados.reduce(
    (total, fichero) => total + (Number(fichero.size) || 0),
    0,
  );

  // Barras: los productos más leídos, con su promedio y su rango.
  const productos = categorias
    .flatMap(([categoria, lista]) =>
      Object.entries(lista).map(([producto, resumen]) => {
        const precios = resumen.precios ?? [];
        const suma = precios.reduce((total, precio) => total + precio, 0);

        return {
          categoria,
          producto,
          lecturas: resumen.cantidad,
          estaciones: resumen.estaciones ?? 0,
          promedio: precios.length ? suma / precios.length : 0,
          minimo: precios.length ? Math.min(...precios) : 0,
          maximo: precios.length ? Math.max(...precios) : 0,
        };
      }),
    )
    .filter((fila) => fila.lecturas > 0)
    .sort((a, b) => b.lecturas - a.lecturas)
    .slice(0, 8);

  // Dona: lecturas por categoría.
  const sectores = categorias
    .map(([categoria, lista]) => ({
      etiqueta: categoria,
      valor: Object.values(lista).reduce((total, resumen) => total + resumen.cantidad, 0),
    }))
    .filter((sector) => sector.valor > 0);

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/pricing"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Pricing
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <TrendingUp className="size-6 text-brand-500" aria-hidden="true" />
          Dashboard
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Precios de Osinergmin (Facilito), su evolución y los ficheros de datos de la zona que
          elijas.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {consulta.isFetching ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-ink-400" role="status">
            <Gauge className="size-3 animate-pulse" aria-hidden="true" />
            Actualizando…
          </span>
        ) : null}

        {datos ? (
          <Badge variant="brand">
            <MapPin className="mr-1 size-3" aria-hidden="true" />
            {filtro.alcance}
          </Badge>
        ) : null}
        {filtro.periodo ? <Badge variant="outline">{filtro.periodo}</Badge> : null}

        {estado ? (
          <Badge variant={estado.server === 'Online' ? 'success' : 'danger'}>
            Servicio {estado.server === 'Online' ? 'en línea' : estado.server}
          </Badge>
        ) : null}
      </div>
    </header>
  );

  if (panel.error && !panel.data) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Servicio de precios no disponible">
          <span className={ICONO_EN_ALERTA}>
            <ServerCog className="size-4" aria-hidden="true" />
            {mensajeDe(
              panel.error,
              'No se pudo contactar con el servicio de precios para leer el dashboard.',
            )}
          </span>
          <span className="mt-2 block text-xs text-ink-500">
            Vuelve a intentarlo en unos minutos; si el problema continúa, avisa al administrador
            del sistema.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="De dónde salen estos datos">
        Los precios se obtienen del portal <em>Facilito</em> de Osinergmin y se publican por
        ubicación y fecha. Las gráficas resumen los datos de la zona y el periodo elegidos; el
        seguimiento de la competencia y la configuración de las consultas sí se guardan en la base.
      </Alert>

      <FiltrosUbicacionCard
        valores={filtro}
        anios={datos?.anios ?? []}
        departamentosConDatos={datos?.departamentos ?? []}
      />

      {consulta.error && !consulta.data ? (
        <Alert variant="warning" title="No se pudieron leer las estadísticas">
          <span className={ICONO_EN_ALERTA}>
            <Database className="size-4" aria-hidden="true" />
            {mensajeDe(consulta.error, 'El servicio de precios no devolvió el consolidado.')}
          </span>
        </Alert>
      ) : null}

      {cargando ? (
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, indice) => (
            <Skeleton key={indice} className="h-24 w-full" />
          ))}
        </div>
      ) : sinDatos ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-ink-100 text-ink-400">
              <LineChart className="size-5" aria-hidden="true" />
            </span>
            <p className="text-sm font-semibold text-ink-900">
              Sin datos para {filtro.alcance}
              {filtro.periodo ? ` en ${filtro.periodo}` : ''}
            </p>
            <p className="max-w-md text-xs text-ink-500">
              Prueba con otra zona, otro mes o vuelve a todo el histórico.
            </p>
            {filtro.hayFiltro ? (
              <button
                type="button"
                onClick={filtro.limpiar}
                className="mt-1 text-xs font-semibold text-brand-700 hover:underline"
              >
                Limpiar filtros
              </button>
            ) : null}
          </CardContent>
        </Card>
      ) : datos ? (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Indicador
              titulo="Estaciones únicas"
              valor={formatNumber(datos.totalEstaciones)}
              detalle={`${formatNumber(datos.totalCargas)} lecturas con precio`}
              icono={Building2}
              tono="brand"
            />
            <Indicador
              titulo="Ficheros"
              valor={formatNumber(archivosFiltrados.length)}
              detalle={`${tamanoLegible(tamanoFiltrado)} de histórico · ${formatNumber(
                archivos?.total ?? 0,
              )} en total`}
              icono={FileText}
              tono="sky"
            />
            <Indicador
              titulo="Precio promedio"
              valor={formatCurrency(datos.precioPromedio)}
              detalle={`mínimo ${formatCurrency(datos.precioMinimo)} · máximo ${formatCurrency(
                datos.precioMaximo,
              )}`}
              icono={CircleDollarSign}
              tono="emerald"
            />
            <Indicador
              titulo="Última lectura"
              valor={fechaCorta(datos.lastUpdate) || '—'}
              detalle={`versión ${estado?.version ?? '—'} · ${formatNumber(
                estado?.archivosDisponibles ?? 0,
              )} ficheros en el servicio`}
              icono={Clock}
              tono="amber"
            />
          </div>

          <Card>
            <CardContent className="space-y-3">
              <header className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                  <LineChart className="size-4 text-brand-500" aria-hidden="true" />
                  Evolución del precio promedio
                </h2>
                <span className="text-[11px] text-ink-400">
                  {formatNumber(serie.length)} {serie.length === 1 ? 'día' : 'días'} con lecturas
                </span>
              </header>

              <GraficoEvolucion datos={serie} />
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardContent className="space-y-3">
                <header className="flex items-center justify-between gap-2">
                  <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <Tags className="size-4 text-brand-500" aria-hidden="true" />
                    Precio promedio por producto
                  </h2>
                  <span className="text-[11px] text-ink-400">S/ por lectura</span>
                </header>

                <GraficoBarras
                  barras={productos.map((fila) => ({
                    etiqueta: fila.producto,
                    valor: Number(fila.promedio.toFixed(2)),
                    detalle: `${formatCurrency(fila.minimo)} – ${formatCurrency(
                      fila.maximo,
                    )} · ${formatNumber(fila.lecturas)} lecturas`,
                  }))}
                  formato={formatCurrency}
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3">
                <header className="flex items-center justify-between gap-2">
                  <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <PieChart className="size-4 text-brand-500" aria-hidden="true" />
                    Participación por categoría
                  </h2>
                  <span className="text-[11px] text-ink-400">lecturas</span>
                </header>

                <GraficoDona datos={sectores} />
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <Card>
              <CardContent className="space-y-3">
                <header className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <Database className="size-4 text-brand-500" aria-hidden="true" />
                    Últimos ficheros recogidos
                  </h2>
                  <Badge variant="outline">
                    {formatNumber(archivosFiltrados.length)} de {formatNumber(archivos?.total ?? 0)}
                  </Badge>
                </header>

                {archivosFiltrados.length === 0 ? (
                  <p className="text-sm text-ink-500">
                    No hay ficheros para esa zona y periodo. Se muestran los del histórico
                    completo al limpiar los filtros.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                          <th className="py-2">Ubicación</th>
                          <th className="py-2">Fecha</th>
                          <th className="py-2 text-right">Tamaño</th>
                        </tr>
                      </thead>
                      <tbody>
                        {archivosFiltrados.slice(0, 6).map((fichero) => (
                          <tr
                            key={fichero.filename}
                            className="border-b border-ink-50 last:border-0"
                          >
                            <td className="py-2 text-ink-700">
                              {String(fichero.ubicacion || '').replace(/_/g, ' / ')}
                            </td>
                            <td className="py-2 text-ink-500">{fechaCorta(fichero.timestamp)}</td>
                            <td className="py-2 text-right text-ink-500">
                              {fichero.sizeFormatted}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                  <Gauge className="size-4 text-brand-500" aria-hidden="true" />
                  Cómo se lee este panel
                </h2>

                <ul className="space-y-2 text-xs text-ink-500">
                  <li>
                    <strong className="text-ink-700">Evolución</strong>: promedio de todas las
                    lecturas de cada día; la banda clara es el rango mínimo–máximo.
                  </li>
                  <li>
                    <strong className="text-ink-700">Productos</strong>: promedio del periodo por
                    producto, con su rango y el número de lecturas.
                  </li>
                  <li>
                    <strong className="text-ink-700">Categorías</strong>: peso de cada categoría
                    sobre el total de lecturas.
                  </li>
                  <li>
                    <strong className="text-ink-700">Filtros</strong>: zona y periodo se aplican a
                    todas las cifras y gráficas de la pantalla.
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {ACCESOS.map((acceso) => (
              <Link
                key={acceso.to}
                to={acceso.to}
                className={cn(
                  'group flex flex-col gap-1 rounded-card border border-ink-100 bg-white p-4',
                  'transition-colors hover:border-brand-200 hover:bg-brand-50/40',
                )}
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                  <acceso.icono className="size-4 text-brand-500" aria-hidden="true" />
                  {acceso.titulo}
                  <ArrowUpRight
                    className="size-3.5 text-ink-300 transition-colors group-hover:text-brand-500"
                    aria-hidden="true"
                  />
                </span>
                <span className="text-xs text-ink-500">{acceso.detalle}</span>
              </Link>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
